import { Button, Text, VSpacer } from '@/components/DesignSystem';
import { QueryKeys } from '@/constants/QueryKeys';
import { Routes } from '@/constants/Routes';
import { useAuthentication } from '@/contexts/dataSync/AuthenticationContext';
import { useProgramPeriodSelector } from '@/hooks/useProgramPeriodSelector';
import { CustomerOverviewReport } from '@/pages/Reports/CustomerOverviewReport';
import { CustomerPointsReport } from '@/pages/Reports/CustomerPointsReport';
import { TierProgressCustomersReport } from '@/pages/Reports/TierProgressCustomersReport';
import { TransactionPointsHistoryReport } from '@/pages/Reports/TransactionPointsHistoryReport';
import { ReportPageTypes } from '@/pages/Reports/reportRouting';
import { RetailerApi } from '@/utilities/api/RetailerApi';
import ExitToApp from '@mui/icons-material/ExitToApp';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Stack } from '@mui/system';
import { hasUserPermission, ReportType, UserPermission } from '@shared/enums';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

const OverviewPageReportTypes: string[] = [
  ReportType.CustomerPoints,
  ReportType.TierProgress,
];

export const ReportBasePage = () => {
  const navigate = useNavigate();
  const { reportType } = useParams();
  const { t } = useTranslation();
  const { isInternal, retailer: authRetailer, user } = useAuthentication();
  const { state } = useLocation();
  const [searchParams] = useSearchParams();
  const [showExportConfirmationDialog, setShowExportConfirmationDialog] = useState(false);
  const hasReportsPermission = isInternal || hasUserPermission(
    user?.permissions, UserPermission.CanAccessReports,
  );

  const retailerIdParam = searchParams.get('retailerId');
  const { data: selectedRetailer, isFetching: isFetchingRetailer } = useQuery(
    [QueryKeys.GET_RETAILER_BY_ID, retailerIdParam],
    () => RetailerApi.get(retailerIdParam as string),
    { enabled: !authRetailer && !!retailerIdParam },
  );
  const retailer = authRetailer ?? selectedRetailer;

  const {
    selectedProgramPeriod,
    setSelectedProgramPeriod,
  } = useProgramPeriodSelector({
    rewardsPrograms: retailer?.rewardsPrograms,
  });

  useEffect(() => {
    if (state?.selectedProgram) {
      setSelectedProgramPeriod(state.selectedProgram);
    }
  }, []);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  if (!reportType) {
    navigate(Routes.REPORTS);
    return null;
  }

  if (!ReportPageTypes.includes(reportType)) {
    navigate(Routes.REPORTS);
    return null;
  }

  if (!retailer) {
    if (isFetchingRetailer) {
      return null;
    }
    navigate(Routes.REPORTS);
    return null;
  }

  if (!OverviewPageReportTypes.includes(reportType) && !hasReportsPermission) {
    navigate(Routes.REPORTS);
    return null;
  }

  const getReport = () => {
    switch (reportType) {
      case ReportType.CustomerPoints:
        return (
          <CustomerPointsReport
            onCloseExportDialog={() => setShowExportConfirmationDialog(false)}
            onProgramChange={setSelectedProgramPeriod}
            retailer={retailer}
            selectedProgram={selectedProgramPeriod}
            showExportConfirmationDialog={showExportConfirmationDialog}
          />
        );
      case ReportType.TierProgress:
        return selectedProgramPeriod && (
          <TierProgressCustomersReport
            onCloseExportDialog={() => setShowExportConfirmationDialog(false)}
            onProgramChange={setSelectedProgramPeriod}
            program={selectedProgramPeriod}
            retailer={retailer}
            showExportConfirmationDialog={showExportConfirmationDialog}
          />
        );
      case ReportType.TransactionPointsHistory:
        return (
          <TransactionPointsHistoryReport
            onCloseExportDialog={() => setShowExportConfirmationDialog(false)}
            retailer={retailer}
            showExportConfirmationDialog={showExportConfirmationDialog}
          />
        );
      case ReportType.CustomerOverview:
        return selectedProgramPeriod && (
          <CustomerOverviewReport
            onCloseExportDialog={() => setShowExportConfirmationDialog(false)}
            onProgramChange={setSelectedProgramPeriod}
            program={selectedProgramPeriod}
            retailer={retailer}
            showExportConfirmationDialog={showExportConfirmationDialog}
          />
        );
    }
  };

  const getPageTitle = () => {
    switch (reportType) {
      case ReportType.CustomerPoints:
        return t($ => $.overview.kpis.title);
      case ReportType.TierProgress:
        return t($ => $.overview.tierProgress.title);
      case ReportType.TransactionPointsHistory:
        return t($ => $.reports.reportType.transactionPointsHistory.name);
      case ReportType.CustomerOverview:
        return t($ => $.reports.reportType.customerOverview.name);
    }
  };

  return (
    <Stack px="24px">
      <Stack alignItems="flex-start" marginLeft="-14px">
        <Button
          onClick={() => navigate({
            pathname: state?.fromOverview ? Routes.RETAILER_OVERVIEW : Routes.REPORTS,
            search: retailerIdParam ? `?retailerId=${retailerIdParam}` : '',
          })}
          startIcon={<ArrowBackIcon />}
          testID="back-button"
          variant="text"
        >
          {t($ => (state?.fromOverview || !hasReportsPermission)
            ? $.reports.backToOverview : $.reports.backToReports)}
        </Button>
      </Stack>
      <Stack direction="row" justifyContent="space-between">
        <Text category="headline-medium">{getPageTitle()}</Text>
        <Button
          onClick={() => setShowExportConfirmationDialog(true)}
          startIcon={<ExitToApp/>}
          testID={`export-${reportType}-button`}
        >
          {t($ => $.common.actions.export)}
        </Button>
      </Stack>
      <VSpacer size="2" />
      {getReport()}
    </Stack>
  );
};
